import apiService from "../ApiCallService";
import ErrorUtils from "src/utils/ErrorUtils";
import CalculationStep from "src/models/calculation-builder/CalculationStep";
import {useEffect, useMemo, useState} from "react";
import {CalcBuilderComment} from "src/models/calculation-builder/CalcBuilderComment";
import FormattingService from "../utils/FormattingService";
import CalculationTPEEntry from "src/models/calculation-builder/CalculationTPEntry";
import {CLI} from "src/models/common/CLI";
import {SavingStatus} from "src/models/common/SavingStatus";
import CONSTANTS from "src/utils/constants";
import {JeTagsModel} from "src/models/calculations/JeTagsModel";
import {ExpressionPart} from "src/models/calculations/CalculationDetailsResult";

/**
 * This class should only contain the API calls related to Calculation Steps
 */
export default class CalculationStepsService {

    createNewCalculationStep(sequence: number, calculationNumber: string, isSubstep: boolean): CalculationStep {
        return {
            stepName: 'Untitled Step',
            expression: '',
            expressionTokens: [] as Array<ExpressionPart>,
            isBeingEdited: false,
            savingStatus: SavingStatus.Unsaved,
            sequence: sequence,
            isSubstep: isSubstep,
            stepId: `newStep${sequence}`,
            calculationNumber: calculationNumber,
            isNew: true,
            valid: true,
        } as CalculationStep;
    }

    getCalculationSteps(calculationNumber?: string, version?: number, stepsNeedRefresh?: boolean, calculationExecutionId?: string): [any[], boolean, string | null] {
        const [result, setResult] = useState([] as any[]);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState(null as string | null);

        useEffect(() => {
            async function fetchData(calcNumber: string) {
                try {
                    setLoading(true);
                    const response = await apiService.getCalcSteps(calcNumber, version == null ? -1 : version, calculationExecutionId);
                    const json = await response.json();
                    const steps = json.steps as any[];
                    setCalcNumberInSteps(calcNumber, steps);
                    copySubStepsToSubRows(calcNumber, steps);
                    setResult(steps);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }

            if (calculationNumber != null && stepsNeedRefresh === true) {
                fetchData(calculationNumber);
            }
        }, [calculationNumber, stepsNeedRefresh]);
        return [result, loading, error]
    }

    getActiveJeTags(calculationNumber?: string, version?: number, getTags: boolean = true): [JeTagsModel | null, boolean, string | null] {
        const [allJeTags, loading, error] = this.getJeTags(calculationNumber, version, getTags);

        const activeJeTags = useMemo(() => {
            if (!allJeTags) return null;
            return {
                ...allJeTags,
                JETags: allJeTags.JETags?.filter(tag => tag.jeTagActive) || []
            };
        }, [allJeTags]);
        
        return [activeJeTags, loading, error];
    }
    
    getJETagsForYTDGrid(calculationNumber?: string, getTags: boolean = true): [JeTagsModel | null, boolean, string | null] {
        const [draftJeTags, draftLoading, draftError] = this.getJeTags(calculationNumber, CONSTANTS.CALCULATION_VERSIONS.DRAFT_VERSION, getTags);

        const hasDraftData = useMemo(() => {
            return draftJeTags && draftJeTags.JETags && draftJeTags.JETags.length > 0;
        }, [draftJeTags]);

        const shouldFallbackToCurrent = !draftLoading && hasDraftData === false

        const [currentJeTags, currentLoading, currentError] = this.getJeTags(calculationNumber, CONSTANTS.CALCULATION_VERSIONS.CURRENT_VERSION, shouldFallbackToCurrent && getTags);

        const result = useMemo(() => {
            return hasDraftData ? draftJeTags : currentJeTags;
        }, [draftJeTags, currentJeTags, hasDraftData]);

        const loading = draftLoading || (shouldFallbackToCurrent && currentLoading);
        const error = draftError || currentError;

        return [result, loading, error];
    }
    
    getJeTags(calculationNumber?: string, version?: number, getTags: boolean = true): [JeTagsModel | null, boolean, string | null] {
        const [result, setResult] = useState(null as JeTagsModel | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState(null as string | null);

        useEffect(() => {
            async function fetchData(calcNumber: string, calcVersion?: number) {
                try {
                    setLoading(true);
                    const response = await apiService.getJeTags(calcNumber, calcVersion ?? CONSTANTS.CALCULATION_VERSIONS.CURRENT_VERSION);
                    const jeTags = await response.json() as JeTagsModel;
                    if(jeTags) {
                        setResult(jeTags);
                    } else {
                        setError('Something went wrong. Unable to fetch JE Tags');
                    }
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }

            if (calculationNumber != null && getTags) {
                fetchData(calculationNumber, version);
            }
        }, [calculationNumber, version, getTags]);
        return [result, loading, error];
    }

    saveCalculationStep(step?: CalculationStep): [any, boolean, string | null] {
        const [result, setResult] = useState(null as any);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState(null as string | null);

        useEffect(() => {
            async function postData(record: CalculationStep) {
                try {
                    setLoading(true);
                    setError(null);
                    const apiModel = {
                        calculationNumber: record.calculationNumber,
                        step: {
                            stepId: record.stepId,
                            sequence: record.sequence,
                            calculationVersion: -1, // The editing version should always be -1
                            expressionTokens: record.expressionTokens,
                            parentId: record.parentId,
                            stepName: record.stepName,
                            tpEntryType: record.tpEntryType,
                            jeTagDescription: record.jeTagDescription,
                            jeTagActive: record.jeTagActive,
                            jeTagType: record.jeTagType,
                            jeTagId: record.jeTagId,
                            priorPeriodJeTagId: record.priorPeriodJeTagId,
                            substeps: record.subRows?.map(x => ({
                                stepId: x.stepId,
                                sequence: x.sequence,
                                calculationVersion: -1, // The editing version should always be -1
                                expressionTokens: x.expressionTokens,
                                parentId: x.parentId,
                                stepName: x.stepName,
                                tpEntryType: x.tpEntryType,
                                jeTagDescription: x.jeTagDescription,
                                jeTagActive: x.jeTagActive,
                                jeTagType: x.jeTagType,
                                jeTagId: x.jeTagId,
                                priorPeriodJeTagId: x.priorPeriodJeTagId
                            }))
                        }
                    }
                    const response = await (record.isNew ?
                        apiService.createCalculationStep(record.calculationNumber, apiModel) :
                        apiService.updateCalculationStep(record.calculationNumber, record.stepId, apiModel));
                    const json = await response.json()
                    json.step.calculationNumber = record.calculationNumber;
                    setResult(json.step);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }

            if (step != null) {
                postData(step);
            }
        }, [step]);
        return [result, loading, error]
    }

    deleteCalculationStep(payload?: any): [any[], boolean, string | null] {
        const [result, setResult] = useState([] as any[]);
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState(null as string | null);

        useEffect(() => {
            async function fetchData(calculationNumber: string, stepId: string) {
                try {
                    setError(null);
                    const response = await apiService.deleteCalculationStep(calculationNumber, stepId);
                    const json = await response.json()
                    setResult(json);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }

            if (payload != null) {
                fetchData(payload.calculationNumber, payload.stepId);
            }
        }, [payload]);
        return [result, loading, error]
    }

    unTagJEFromCalculationStep(calculationNumber?: string, stepId?: string, shouldUnTag?: boolean): [CalculationStep | null, boolean, string | null] {
        const [result, setResult] = useState(null as CalculationStep | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState(null as string | null);

        useEffect(() => {
            async function fetchData(calculationNumber: string, stepId: string) {
                try {
                    setError(null);
                    const response = await apiService.unTagJEFromCalculationStep(calculationNumber, stepId);
                    const json = await response.json() as CalculationStep
                    setResult(json);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }

            if (calculationNumber != null && stepId != null && shouldUnTag) {
                setLoading(true);
                fetchData(calculationNumber, stepId);
            }
        }, [calculationNumber, stepId, shouldUnTag]);
        return [result, loading, error]
    }

    validateCalculationSteps(calculationNumber?: string, stepsNeedValidation?: boolean): [any, boolean, string | null] {
        const [result, setResult] = useState(null as any);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState(null as string | null);
        //console.log("validateCalculationSteps args: ", arguments);

        useEffect(() => {
            async function fetchData(calcNumber: string) {
                try {
                    setError(null);
                    const response = await apiService.validateCalcSteps(calcNumber);
                    const json = await response.json();
                    const steps = json.steps as any[];
                    setCalcNumberInSteps(calcNumber, steps);
                    copySubStepsToSubRows(calcNumber, steps);
                    setResult({...json, steps: steps});
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }

            if (calculationNumber != null && stepsNeedValidation === true) {
                setLoading(true);
                fetchData(calculationNumber);
            }
        }, [calculationNumber, stepsNeedValidation]);
        return [result, loading, error]
    }

    cancelStepsChanges(calculationNumber?: string, newCancelRequest?: string): [any[], boolean, string | null] {
        const [result, setResult] = useState([] as any[]);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState(null as string | null);

        useEffect(() => {
            async function fetchData(calcNumber: string) {
                try {
                    setError(null);
                    const response = await apiService.cancelCalcStepsChanges(calcNumber);
                    const json = await response.json();
                    const steps = json.steps as any[];
                    setCalcNumberInSteps(calcNumber, steps);
                    copySubStepsToSubRows(calcNumber, steps);
                    setResult(steps);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }

            if (calculationNumber != null && newCancelRequest != null) {
                setLoading(true);
                fetchData(calculationNumber);
            }
        }, [calculationNumber, newCancelRequest]);
        return [result, loading, error]
    }

    runCalSteps(calculationNumber?: string, version?: number, generateJournals?: boolean, shouldDoRun?: string): [any[], boolean, string | null] {
        const [result, setResult] = useState(null as any);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState(null as string | null);

        useEffect(() => {
            async function fetchData(calcNumber: string) {
                try {
                    setError(null);
                    const response = await apiService.runCalculationSteps(calcNumber, version == null ? -1 : version, generateJournals || false);
                    const json = await response.json();
                    formatBreakdownValues(json.results);

                    setResult(json.results);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }

            if (calculationNumber != null && shouldDoRun != null) {
                setLoading(true);
                fetchData(calculationNumber);
            }
        }, [calculationNumber, shouldDoRun]);
        return [result, loading, error]
    }

    getStepComments(calculationNumber?: string, version?: number, stepId?: string, pullStepComments?: boolean): [CalcBuilderComment[], boolean, string | null] {
        const [result, setResult] = useState([] as CalcBuilderComment[]);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState(null as string | null);

        useEffect(() => {
            async function fetchData(calcNumber: string) {
                try {
                    setLoading(true);
                    const response = await apiService.getCalcStepComments(calcNumber, version == null ? -1 : version, stepId || '');
                    const json = await response.json();
                    setResult(json.comments);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }

            if (calculationNumber != null && pullStepComments === true) {
                fetchData(calculationNumber);
            }
        }, [calculationNumber, pullStepComments]);
        return [result, loading, error]
    }

    saveStepComment(comment?: CalcBuilderComment): [CalcBuilderComment | null, boolean, string | null] {
        const [result, setResult] = useState(null as CalcBuilderComment | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState(null as string | null);

        useEffect(() => {
            async function postData(payload: CalcBuilderComment) {
                try {
                    setError(null);
                    const response = await apiService.createCalcStepComment(payload.calculationNumber, payload.calculationVersion as number, payload.stepId || '', payload.comment);
                    const json = await response.json();
                    setResult(json.comment);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }

            if (comment != null) {
                setLoading(true);
                postData(comment);
            }
        }, [comment]);
        return [result, loading, error]
    }

    getTPEntries(calculation?: CLI, pullEntries?: boolean): [CalculationTPEEntry[], boolean, string | null] {
        const [result, setResult] = useState([] as CalculationTPEEntry[]);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState(null as string | null);

        useEffect(() => {
            async function fetchData(cli: CLI) {
                try {
                    setLoading(true);
                    const response = await apiService.getTPEntries(cli.calculationNumber, cli.providerJurisdiction === "India");
                    const json = await response.json();
                    setResult(json.tpEntries.map((x: any) => mapToCalculationTPEEntry(x, cli)));
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }

            if (calculation != null && pullEntries) {
                fetchData(calculation);
            }
        }, [calculation, pullEntries]);
        return [result, loading, error]
    }
}

function setCalcNumberInSteps(calculationNumber: string, steps: any[]) {
    steps.forEach(s => {
        s.calculationNumber = calculationNumber;
    })
}

function copySubStepsToSubRows(calculationNumber: string, steps: any[]) {
    // copying items from substeps to subRows since React-grid looks for "subRows" property when rendering nested rows
    steps.forEach(s => {
        s.subRows = s.substeps;
        if (s.subRows) {
            const subRows = s.subRows as any[];
            subRows.forEach(sub => {
                sub.isSubstep = true;
                sub.calculationNumber = calculationNumber;
            })
        }
    })
}

function formatBreakdownValues(runResults: any[]) {
    const formattingService = new FormattingService();
    runResults.forEach((r) => {
        const formattedBreakdown: string[] = [];
        r.valueBreakdown.split(' ').forEach((b: string) => {
            if (b != '') {
                if (!isNaN(+b)) {
                    formattedBreakdown.push(formattingService.formatString(b, true, 10));
                } else {
                    formattedBreakdown.push(b);
                }
            }
        });
        r.valueBreakdown = formattedBreakdown.join(' ');
    })
}

function mapToCalculationTPEEntry(x: any, cli: CLI): CalculationTPEEntry | null {
    if (x == null) {
        return null;
    }
    return {
        beatQualifier: x.beatQualifier,
        name: x.name,
        displayName: x.priorPeriodJeTagId ? `${x.name}-JE ${x.priorPeriodJeTagId}` : x.name,
        value: getTPEntryValue(x, cli),
        category: 'TP Charge',
        priorPeriodJeTagId: x.priorPeriodJeTagId
    }
}

function getTPEntryValue(entry: any, cli: CLI): string | undefined {
    switch (entry.name) {
        case "Apttus Markup Rate":
            return cli.tpRate == null || isNaN(+cli.tpRate) ? "0" : (parseFloat(cli.tpRate) / 100).toString();
        default:
            return entry.value == null ? undefined : "" + entry.value;
    }
}

